import React, {useEffect, useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Message} from "primereact/message";
import {ProgressBar} from "primereact/progressbar";
import {ProgressSpinner} from "primereact/progressspinner";
import {Tag} from "primereact/tag";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import Span from "../../../system/components/span";
import {useTranslate} from "../../../system/useTranslate";
import {useTeam} from "../../../models/team/use-team";
import BoxHeader from "../../components/box-header";
import PageHeader from "../../components/page-header";
import ProjectSidebar from "../../projects/components/project-sidebar";

const STATUS_SEVERITY = {"on-way": "success", "has-risks": "warning", "out-of-way": "danger", achieved: "success", canceled: "secondary", archived: "secondary"};

function Projects({teamData}) {
    const translate = useTranslate();
    const teamApi = useTeam();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProject, setSelectedProject] = useState();
    const [showHistory, setShowHistory] = useState(false);

    const load = async () => {
        setLoading(true);
        setError(null);
        try { setSummary(await teamApi.getTeamSummary(teamData.documentId)); }
        catch (requestError) { setError(requestError?.message || "team.projects.load-error"); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamData?.documentId]);

    const visibleProjects = useMemo(() => (summary?.projects || []).filter((project) => showHistory
        ? ["achieved", "canceled", "archived"].includes(project.projectStatus)
        : !["achieved", "canceled", "archived"].includes(project.projectStatus)), [summary?.projects, showHistory]);

    if (loading) return <div className="h-full flex align-items-center justify-content-center"><ProgressSpinner/></div>;
    if (error) return <div className="h-full flex flex-column align-items-center justify-content-center gap-3">
        <Message severity="error" text={translate(error)}/>
        <Button outlined className="text-xs gap-2" icon="pi pi-refresh" label={translate("team.retry")} onClick={load}/>
    </div>;

    const cards = [
        ["team.projects.count.all", summary?.counts?.all || 0, "pi pi-folder"],
        ["team.projects.count.active", summary?.counts?.active || 0, "pi pi-play-circle"],
        ["team.projects.count.completed", summary?.counts?.completed || 0, "pi pi-check-circle"],
        ["team.projects.count.review", summary?.counts?.review || 0, "pi pi-exclamation-circle"],
    ];

    const ownerBody = (project) => <div className="flex align-items-center gap-2"><Avatar memberData={project?.owner?.member}/><span>{project?.owner?.member?.fullname || translate("team.member.unknown")}</span></div>;
    const progressBody = (project) => project.progress === null
        ? <span className="text-500">{translate("team.projects.progress-unavailable")}</span>
        : <div className="flex align-items-center gap-2"><ProgressBar className="w-8rem h-1rem text-2xs" value={project.progress}/><span>{project.progress}%</span></div>;
    const statusBody = (project) => <Tag severity={STATUS_SEVERITY[project.projectStatus] || "info"} value={translate(project.projectStatus)}/>;
    const dateBody = (project) => project.doneAt ? <Span type="PERSIAN_DATE" format="YYYY/MM/DD">{project.doneAt}</Span> : "—";
    const actionBody = (project) => <Button text rounded icon={project.restricted ? "pi pi-lock" : "pi pi-eye"} disabled={project.restricted}
        aria-label={translate(project.restricted ? "team.projects.restricted" : "team.projects.view")} onClick={() => setSelectedProject(project)}/>;

    return <div className="h-full overflow-auto surface-50 p-3 flex flex-column gap-4">
        <PageHeader title={translate("overview-team-projects")} description={translate("overview-team-projects-description")}/>
        <div className="grid">
            {cards.map(([label, value, icon]) => <div key={label} className="col-12 md:col-6 lg:col-3">
                <article className="surface-card border-1 border-100 border-round-xl shadow-1 p-4 flex align-items-center gap-3 h-full">
                    <span className="w-3rem h-3rem border-circle surface-100 flex align-items-center justify-content-center"><i className={`${icon} text-primary text-xl`}/></span>
                    <div><small className="block text-500 mb-2">{translate(label)}</small><strong className="text-2xl text-900">{value.toLocaleString("fa-IR")}</strong></div>
                </article>
            </div>)}
        </div>

        <section>
            <BoxHeader className="pr-2" icon={<Icon icon="mi mi:info" className="text-red-500"/>} title={translate("emergency-priorities")}/>
            {summary?.priorities?.length ? <div className="grid">
                {summary.priorities.map((project) => <div key={project.documentId || project.id} className="col-12 lg:col-6">
                    <article className="surface-card border-1 border-red-100 border-right-3 border-red-500 border-round-xl p-3 flex align-items-center gap-3 shadow-1">
                        <span className="w-3rem h-3rem border-circle bg-red-50 text-red-500 flex align-items-center justify-content-center"><i className="pi pi-clock"/></span>
                        <div className="flex-1 min-w-0"><strong className="block text-sm text-900 mb-1">{project.title}</strong>
                            <small className="text-500">{translate(project.overdueCount ? "team.projects.overdue-count" : "team.projects.risk-status").replace("{{COUNT}}", Number(project.overdueCount || 0).toLocaleString("fa-IR"))}</small></div>
                        <Button text className="text-xs gap-2" icon="pi pi-arrow-left" label={translate("check")} onClick={() => setSelectedProject(project)}/>
                    </article>
                </div>)}
            </div> : <div className="surface-card border-1 border-100 border-round-xl p-4 text-center text-500">{translate("team.projects.priorities-empty")}</div>}
        </section>

        <section className="surface-card border-1 border-100 border-round-xl shadow-1 overflow-hidden">
            <div className="p-3 flex align-items-center justify-content-between border-bottom-1 border-100">
                <strong>{translate(showHistory ? "projects-history" : "projects-list")}</strong>
                <Button outlined className="text-xs gap-2" icon={showHistory ? "pi pi-list" : "pi pi-history"}
                    label={translate(showHistory ? "projects-list" : "projects-history")} onClick={() => setShowHistory((current) => !current)}/>
            </div>
            <DataTable value={visibleProjects} paginator rows={10} responsiveLayout="scroll" className="text-xs"
                emptyMessage={translate(showHistory ? "team.projects.history-empty" : "team.projects.empty")}>
                <Column field="title" header={translate("project-title")}/>
                <Column header={translate("manager")} body={ownerBody}/>
                <Column header={translate("progress")} body={progressBody}/>
                <Column header={translate("status")} body={statusBody}/>
                {showHistory && <Column header={translate("doneCancelDate")} body={dateBody}/>}
                <Column header={translate("operations")} body={actionBody}/>
            </DataTable>
        </section>
        <ProjectSidebar projectData={selectedProject} setProjectData={setSelectedProject} onHide={() => setSelectedProject(undefined)}/>
    </div>;
}

export default Projects;

